<template>
  <div style="background-color: #F6F3F4;">
    <SearchHeader v-model="searchInputValue" @doSearch="handleFormSubmit()" />
    <div class="container">
      <!-- result header start -->
      <div class="row search-result--header">
        <div class="col-xl-6 col-12 mb-xl-0 mb-2">
          <SearchPagingInfo :search-state="searchState" />
        </div>
        <div class="col-xl-6 col-12 search-result--options">
          <div class="col">
            <SearchResultsPerPage
              v-show="thereAreResults"
              v-model.number="resultsPerPage"
            />
          </div>
          <div class="col">
            <SearchSort v-show="thereAreResults" v-model="sortBy" />
          </div>
          <div class="col">
            <SearchDisplay v-show="thereAreResults" v-model="displayBy" />
          </div>
        </div>
      </div>
      <!-- result header end -->
      <div class="row">
        <div v-if="searchState.wasSearched" class="col-xl-2 col-12 search-facets">
          <!-- Button trigger modal -->
          <button type="button" class="btn btn-primary" @click.prevent="show_modal = true">
            Filter
          </button>
          <div
            :class="{'show': show_modal, 'modal fade': isTabletOrSmaller}"
            role="dialog"
            aria-labelledby="filterModalLabel"
            aria-hidden="true"
          >
            <div :class="{'modal-header': isTabletOrSmaller}">
              <button type="button" class="close" aria-label="Close" @click.prevent="show_modal = false"><span aria-hidden="true">&times;</span></button>
            </div>
            <!-- filter section start -->
            <p class="h5">Study levels</p>
            <SearchFacet
              :checked="facetsData.study_level"
              :facet="searchState.facets.study_level[0]"
              @change="handleFacetChange($event, 'study_level')"
            />
            <p class="h5">Curriculum type</p>
            <SearchFacet
              :checked="facetsData.curriculum_item_type"
              :facet="searchState.facets.curriculum_item_type[0]"
              @change="handleFacetChange($event, 'curriculum_item_type')"
            />
            <p class="h5">Study areas</p>
            <SearchFacet
              :checked="facetsData.study_area"
              :facet="searchState.facets.study_area[0]"
              @change="handleFacetChange($event, 'study_area')"
            />
            <!-- filter section end -->
            <div :class="{'modal-footer': isTabletOrSmaller}">
              <button type="button" class="btn btn-secondary" @click.prevent="show_modal = false">Close</button>
            </div>
          </div>
        </div>
        <div v-if="searchState.wasSearched" class="col-xl-10 col-12 search-results">
          <!-- result section start -->
          <SearchResults :search-input-value="searchInputValue" v-show="thereAreResults" :results="searchState.results" :display-class="displayBy" @linkClicked="onClickLink" />
          <div v-show="!thereAreResults">
            <p class="h3">No results</p>
            <p>Your search for <strong>{{ searchState.searchTerm }}</strong> did not return any results.</p>
          </div>
          <!-- result section start -->
        </div>
        <div class="search-results--pagination">
          <SearchPagination
            v-show="thereAreResults"
            :total-pages="Math.min(searchState.totalPages, 100)"
            :click-handler="setCurrentPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { watch, computed, onMounted, onUnmounted, ref, reactive } from 'vue'
import config from "/client/js/searchConfig.js";
import SearchHeader from "./SearchHeader.vue";
import SearchSort from "./SearchSort.vue";
import SearchDisplay from "./SearchDisplay.vue";
import SearchFacet from "./SearchFacet.vue";
import SearchResults from "./SearchResults.vue";
import SearchPagingInfo from "./SearchPagingInfo.vue";
import SearchPagination from "./SearchPagination.vue";
import SearchResultsPerPage from "./SearchResultsPerPage.vue";
import { SearchDriver } from "@elastic/search-ui";

const driver = new SearchDriver(config)

const searchInputValue = ref('')
const searchState = ref({})
const facetsData = reactive({
      study_level: [],
      curriculum_item_type: [],
      study_area: []
})
const resultsPerPage = ref(9)
const sortBy = ref("relevance")
const displayBy = ref("col-xxl-4 col-xl-6 col-12")

//modal
let show_modal = ref(false)

// Watch for certain state changes in "search" store
watch(resultsPerPage, (newResultsPerPage) => driver.setResultsPerPage(newResultsPerPage))
watch(sortBy, (newSortBy) => driver.setSort(newSortBy, 'asc'))

// Computed values
const thereAreResults = computed(() => { return searchState.value.totalResults && searchState.value.totalResults > 0 })

// check screen size
let isTabletOrSmaller = ref(window.innerWidth <= 1200);
const updateViewport = () => {
  isTabletOrSmaller.value = window.innerWidth <= 1200;
};

onMounted(() => {
  const { searchTerm, sortField, resultsPerPage: defaultResultsPerPage, filters, facets } = driver.getState()

  console.log('Mounted', { searchTerm, sortField, defaultResultsPerPage, filters, facets } )
  // restoring UI from url query
  searchInputValue.value = searchTerm;
  sortBy.value = sortField;
  resultsPerPage.value = defaultResultsPerPage % 10 == 0 ? 9 : defaultResultsPerPage;
  filters.forEach(filter => {
    if (filter.type === "range") {
      facetsData[filter.field] = filter.values.map(value => value.name);
    } else {
      facetsData[filter.field] = filter.values;
    }
  });

  driver.subscribeToStateChanges(state => {
    searchState.value = state
  })

  // add viewport event listener
  window.addEventListener('resize', updateViewport);
})

onUnmounted(() => {
  window.removeEventListener('resize', updateViewport);
})

function handleFormSubmit() {
  //if(searchInputValue.value)
    driver.getActions().setSearchTerm(searchInputValue.value)
}

function handleFacetChange(event, facet) {
  const { value, checked } = event.target
  const facetFromDriver = driver.getState().facets[facet][0]
  const valueforApi =
    facetFromDriver.type === "range"
        ? facetFromDriver.data.find(item => item.value.name === value).value
        : value

  if (checked) {
    facetsData[facet].push(value)
    driver.addFilter(facet, valueforApi, "any")
  } else {
    const index = facetsData[facet].indexOf(value)
    if (index > -1) {
        facetsData[facet].splice(index, 1)
    }
    driver.removeFilter(facet, valueforApi, "any")
  }
}

function setCurrentPage(page) {
  driver.setCurrent(page)
}

//Elastic Click Analytics
const onClickLink = (e) => {
  // Call your tracking function here
  //const event = e.target.dataset.event;
  const id = e.currentTarget.dataset.category;
  //const type = e.target.dataset.type;

  //let originalValue = e.target.dataset.value;
  //let newValue = originalValue.replace(/\s+/g, '_');
  //const tags = []; //[event, type, newValue];
  trackClick(id);
}

function trackClick(id) {
  // Send the click-through event to your analytics or tracking system
  driver.getActions().trackClickThrough(id);
  //console.log('trackClick called with:', { event, category, type, value });
}
</script>